import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useMemberAuth, memberApi } from "../lib/memberAuth";

const STEPS = ["Tier", "Your Details", "Profession", "Tell Us More", "Review"];

export default function MembershipApply() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { account, idStatus, loading: authLoading } = useMemberAuth();
  const initialTier = params.get("tier") || "founders";

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    membership_type: initialTier,
    full_name: "",
    email: "",
    phone: "",
    occupation: "",
    company: "",
    linkedin: "",
    referral: "",
    why_join: "",
  });

  useEffect(() => { setForm((f) => ({ ...f, membership_type: initialTier })); }, [initialTier]);
  useEffect(() => {
    if (account) setForm((f) => ({ ...f, email: f.email || account.email, full_name: f.full_name || account.full_name || "", phone: f.phone || account.phone || "" }));
  }, [account]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 0) return !!form.membership_type;
    if (step === 1) return form.full_name && form.email && form.phone;
    if (step === 2) return form.occupation;
    if (step === 3) return form.why_join && form.why_join.length >= 30;
    return true;
  };

  const next = () => {
    if (!validateStep()) { toast.error("Please complete this step before continuing."); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      await memberApi.post("/membership/apply", form);
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "ID_REQUIRED" || detail === "ID_REJECTED") {
        toast.error("Please verify your age (upload your ID) in your account first.");
      } else {
        toast.error("Submission failed. Please try again.");
      }
    } finally { setSubmitting(false); }
  };

  if (!authLoading && !account) {
    return (
      <GateScreen
        testId="apply-auth-required"
        title="Create your account first."
        body="Membership applications require a free Win Room account with one-time age verification (21+)."
        ctaLabel="Sign In / Create Account"
        ctaTo="/account/login?next=/membership/apply"
        Icon={UserRound}
      />
    );
  }

  if (!authLoading && account && (idStatus === "none" || idStatus === "rejected")) {
    return (
      <GateScreen
        testId="apply-id-required"
        title="Verify your age."
        body={idStatus === "rejected"
          ? "Your ID could not be verified. Please upload a new photo of your government-issued ID to continue your application."
          : "One quick step before applying: upload a photo of your government-issued ID to confirm you're 21+. It stays securely on file."}
        ctaLabel="Upload Your ID"
        ctaTo="/account"
        Icon={ShieldCheck}
      />
    );
  }

  if (done) {
    return (
      <section className="min-h-screen pt-40 pb-24 px-6 flex items-center justify-center" data-testid="apply-thankyou">
        <div className="max-w-xl text-center">
          <div className="monogram mx-auto" style={{ width: 64, height: 64, fontSize: 16 }}>TWR</div>
          <p className="eyebrow mt-6">Application Received</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Thank you.</h1>
          <p className="text-[var(--twr-warm-white)]/70 mt-5 leading-relaxed">
            Your application is now with the Founding Committee. We review submissions in confidential waves, and you'll
            hear from our membership team within ten business days.
          </p>
          <button onClick={() => navigate("/")} data-testid="apply-return-home" className="btn-bronze mt-10">Return Home</button>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen pt-32 pb-24 px-6 sm:px-10 lg:px-16 bg-[#0A0A0A]" data-testid="apply-page">
      <div className="max-w-3xl mx-auto">
        <p className="eyebrow">Membership Application</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">An invitation, quietly extended.</h1>

        {/* Progress */}
        <div className="mt-12 flex items-center gap-3">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-3 flex-1">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-[11px] font-medium ${i <= step ? "bg-[var(--twr-bronze)] text-[#0A0A0A]" : "border border-[var(--twr-line-strong)] text-[var(--twr-warm-white)]/55"}`}>
                {i < step ? <Check size={13} /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-[var(--twr-bronze)]" : "bg-[var(--twr-line)]"}`} />}
            </div>
          ))}
        </div>
        <p className="text-[11px] tracking-[0.28em] uppercase text-[var(--twr-warm-white)]/55 mt-4">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>

        <div className="surface p-8 sm:p-12 mt-10">
          {step === 0 && (
            <div className="space-y-4" data-testid="apply-step-tier">
              <h2 className="font-serif text-2xl mb-6">Choose your membership tier</h2>
              {[
                { id: "founders", title: "Founders 100", sub: "Lifetime recognition. Permanent wall." },
                { id: "executive", title: "Executive", sub: "Professional membership." },
                { id: "corporate", title: "Corporate", sub: "For executive teams." },
              ].map((t) => (
                <button key={t.id} type="button"
                  data-testid={`apply-tier-${t.id}`}
                  onClick={() => set("membership_type", t.id)}
                  className={`w-full text-left flex items-center justify-between p-5 border transition-colors ${form.membership_type === t.id ? "border-[var(--twr-bronze)] bg-[var(--twr-bronze)]/5" : "border-[var(--twr-line)] hover:border-[var(--twr-line-strong)]"}`}
                >
                  <div>
                    <p className="font-serif text-xl">{t.title}</p>
                    <p className="text-[var(--twr-warm-white)]/60 text-sm mt-1">{t.sub}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border ${form.membership_type === t.id ? "border-[var(--twr-bronze)] bg-[var(--twr-bronze)]" : "border-[var(--twr-line-strong)]"}`} />
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="apply-step-details">
              <Field label="Full Name" value={form.full_name} onChange={(v) => set("full_name", v)} testId="apply-full-name" required />
              <Field label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testId="apply-email" required />
              <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testId="apply-phone" required />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="apply-step-profession">
              <Field label="Occupation / Title" value={form.occupation} onChange={(v) => set("occupation", v)} testId="apply-occupation" required />
              <Field label="Company" value={form.company} onChange={(v) => set("company", v)} testId="apply-company" />
              <Field label="LinkedIn URL" value={form.linkedin} onChange={(v) => set("linkedin", v)} testId="apply-linkedin" />
              <Field label="Referred by (Member name, if applicable)" value={form.referral} onChange={(v) => set("referral", v)} testId="apply-referral" />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="apply-step-why">
              <label className="block">
                <span className="eyebrow">Why join?</span>
                <textarea
                  data-testid="apply-why-join"
                  value={form.why_join} onChange={(e) => set("why_join", e.target.value)}
                  rows={6} required minLength={30}
                  placeholder="Tell us about yourself, what draws you to The Win Room, and how you'd like to contribute to the community…"
                  className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-base leading-relaxed transition-colors"
                />
                <span className="text-[11px] text-[var(--twr-warm-white)]/55 mt-2 block">{form.why_join.length}/30 characters minimum</span>
              </label>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="apply-step-review">
              <h2 className="font-serif text-2xl mb-6">Review your application</h2>
              {[
                ["Tier", form.membership_type],
                ["Name", form.full_name], ["Email", form.email], ["Phone", form.phone],
                ["Occupation", form.occupation], ["Company", form.company || "—"],
                ["LinkedIn", form.linkedin || "—"], ["Referral", form.referral || "—"],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between py-3 border-b border-[var(--twr-line)]">
                  <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55">{l}</span>
                  <span className="text-sm">{v}</span>
                </div>
              ))}
              <div className="py-3">
                <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55">Why Join</span>
                <p className="text-sm mt-2 leading-relaxed">{form.why_join}</p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button onClick={back} disabled={step === 0} data-testid="apply-back-btn"
            className="btn-ghost disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronLeft size={14} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} data-testid="apply-next-btn" className="btn-bronze">Continue <ChevronRight size={14} /></button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="apply-submit-btn" className="btn-bronze">
              {submitting ? "Submitting…" : "Submit Application"} <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function GateScreen({ testId, title, body, ctaLabel, ctaTo, Icon }) {
  return (
    <section className="min-h-screen pt-40 pb-24 px-6 flex items-center justify-center" data-testid={testId}>
      <div className="max-w-xl text-center">
        <Icon size={32} strokeWidth={1.2} className="text-[var(--twr-bronze)] mx-auto" />
        <p className="eyebrow mt-6">Membership Application</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">{title}</h1>
        <p className="text-[var(--twr-warm-white)]/70 mt-5 leading-relaxed">{body}</p>
        <Link to={ctaTo} className="btn-bronze mt-10 inline-flex">{ctaLabel} <ChevronRight size={14} /></Link>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}{required && " *"}</span>
      <input
        data-testid={testId}
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-base transition-colors"
      />
    </label>
  );
}
